import React, { useState } from 'react';
import {
  BookOpen,
  Sparkles,
  Star,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Video,
  Feather,
  Globe,
  Printer,
  TrendingUp,
  Send,
  Zap,
  ShieldCheck,
  Layers,
  BookMarked,
  Layout,
  MessageSquare,
  Phone,
  FileText,
  ChevronDown,
  Crown,
  Camera,
  DollarSign,
  HelpCircle,
  Award,
  Sliders,
  Check,
  Share2
} from 'lucide-react';
import { COMPANY_INFO } from '../data/companyData';
import { SEO } from './SEO';

interface SelfPublishingPageProps {
  onBackToHome: () => void;
  onOpenContact: (initialService?: string) => void;
}

export const SelfPublishingPage: React.FC<SelfPublishingPageProps> = ({
  onBackToHome,
  onOpenContact
}) => {
  // Calculator State
  const [bookPrice, setBookPrice] = useState<number>(499);
  const [salesVolume, setSalesVolume] = useState<number>(1000);
  const [bookFormat, setBookFormat] = useState<'paperback' | 'hardcover' | 'ebook' | 'bundle'>('paperback');
  const [activeTab, setActiveTab] = useState<'creators' | 'authors' | 'poets' | 'business' | 'magazines'>('creators');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // Form State
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    authorName: '',
    email: '',
    phone: '',
    creatorType: 'YouTuber / Content Creator',
    bookGenre: 'Self-Help & Personal Growth',
    workingTitle: '',
    estimatedPages: '150 - 250 Pages',
    manuscriptStatus: 'Complete Manuscript Ready',
    servicesNeeded: ['Cover & Interior Design', 'Global Distribution (Amazon & IngramSpark)'],
    targetLaunch: 'Within 30 Days',
    notes: ''
  });

  // Calculate Royalty Estimates
  const calculatePrintCost = () => {
    switch (bookFormat) {
      case 'paperback': return 120;
      case 'hardcover': return 220;
      case 'ebook': return 0;
      case 'bundle': return 240;
      default: return 120;
    }
  };

  const unitPrintCost = calculatePrintCost();
  const grossRevenue = bookPrice * salesVolume;
  const totalPrintCost = unitPrintCost * salesVolume;
  const netProfit = Math.max(0, grossRevenue - totalPrintCost);
  const profitMargin = bookPrice > 0 ? Math.round(((bookPrice - unitPrintCost) / bookPrice) * 100) : 0;

  const handleCheckboxToggle = (service: string) => {
    if (formData.servicesNeeded.includes(service)) {
      setFormData({
        ...formData,
        servicesNeeded: formData.servicesNeeded.filter((s) => s !== service)
      });
    } else {
      setFormData({
        ...formData,
        servicesNeeded: [...formData.servicesNeeded, service]
      });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Store lead locally
    const newLead = {
      fullName: formData.authorName,
      email: formData.email,
      phone: formData.phone,
      company: `${formData.creatorType} (${formData.bookGenre})`,
      serviceCategory: 'Self-Publishing & Author Services',
      projectScope: formData.estimatedPages,
      message: `Working Title: ${formData.workingTitle || 'Untitled'}\nStatus: ${formData.manuscriptStatus}\nServices: ${formData.servicesNeeded.join(', ')}\nLaunch: ${formData.targetLaunch}\nNotes: ${formData.notes}`,
      id: Date.now().toString(),
      timestamp: new Date().toLocaleString()
    };

    try {
      const existing = JSON.parse(localStorage.getItem('asap_learning_leads') || '[]');
      localStorage.setItem('asap_learning_leads', JSON.stringify([newLead, ...existing]));

      const authorRecord = {
        id: `AUTH-${Date.now()}`,
        authorName: formData.authorName,
        email: formData.email,
        phone: formData.phone,
        creatorType: formData.creatorType,
        bookGenre: formData.bookGenre,
        workingTitle: formData.workingTitle || 'Untitled Manuscript',
        estimatedBudget: formData.estimatedPages || '200 Pages (Paperback)',
        notes: `Manuscript Status: ${formData.manuscriptStatus}\nServices Needed: ${formData.servicesNeeded.join(', ')}\nTarget Launch: ${formData.targetLaunch}\nNotes: ${formData.notes}`,
        status: 'New Inquiry',
        dateSubmitted: new Date().toISOString().split('T')[0]
      };
      const existingAuthors = JSON.parse(localStorage.getItem('asap_author_inquiries') || '[]');
      localStorage.setItem('asap_author_inquiries', JSON.stringify([authorRecord, ...existingAuthors]));
    } catch (err) {
      console.error('Error storing author lead:', err);
    }

    // Submit lead to server API and contact@asaplearning.in email dispatch
    try {
      await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          fullName: formData.authorName,
          email: formData.email,
          phone: formData.phone,
          organization: `${formData.creatorType} (${formData.bookGenre})`,
          serviceRequirement: 'Self-Publishing & Author Services',
          estimatedBudget: formData.estimatedPages,
          message: `Title: ${formData.workingTitle}\nStatus: ${formData.manuscriptStatus}\nServices: ${formData.servicesNeeded.join(', ')}\nNotes: ${formData.notes}`
        })
      });

      await fetch(`https://formsubmit.co/ajax/${COMPANY_INFO.email}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          _subject: `New Author/Creator Publishing Inquiry: ${formData.authorName}`,
          author_name: formData.authorName,
          author_email: formData.email,
          author_phone: formData.phone,
          creator_type: formData.creatorType,
          book_genre: formData.bookGenre,
          working_title: formData.workingTitle || 'N/A',
          manuscript_status: formData.manuscriptStatus,
          services_requested: formData.servicesNeeded.join(', '),
          target_launch: formData.targetLaunch,
          notes: formData.notes,
          destination_email: 'contact@asaplearning.in'
        })
      });
    } catch (err) {
      console.log('Publishing transmission note:', err);
    }

    setFormSubmitted(true);
  };

  // Creator Archetypes Data
  const creatorArchetypes = [
    {
      id: 'creators',
      title: 'YouTubers & Content Creators',
      badge: 'Monetize Fanbase',
      icon: <Video className="w-6 h-6 text-rose-500" />,
      tagline: 'Turn Your Channel Wisdom, Courses & Community Stories into a Physical Hardcover Bestseller',
      description: 'Transform your top performing video series, podcasts, life lessons, and frameworks into a high-margin physical book. Perfect for merch bundles, channel anniversaries, and VIP fan experiences.',
      sampleBooks: ['The Creator Playbook', 'From 0 to 1M Subscribers', 'Mindset Hacks for Creators'],
      highlights: [
        'Custom Merch-Ready Hardback & Foil Embossed Covers',
        'QR Code QR-Link Integrations to Channel Videos & Bonus Courses',
        'Bulk Fan Shipping & Author Proof Copies for Unboxing Videos',
        '100% Revenue Kept by You — No Publisher Royalty Cut'
      ]
    },
    {
      id: 'authors',
      title: 'Self-Help & Personal Growth Authors',
      badge: 'High Impact',
      icon: <Sparkles className="w-6 h-6 text-amber-500" />,
      tagline: 'Empower Readers with Clean Workbooks, Exercises, and Transformational Frameworks',
      description: 'Give your coaching methodologies, wellness practices, and motivational guides the premium typography and paper quality they deserve. Perfect for workshop hand-outs and Amazon Bestseller launches.',
      sampleBooks: ['The Unstoppable Mind', 'Daily Habits of High Performers', 'Financial Freedom Roadmap'],
      highlights: [
        'Interactive Page Layouts, Worksheets & Reflection Spreads',
        'Matte Soft-Touch & Hardcover Trade Paperback Production',
        'Amazon KDP, Barnes & Noble & Global IngramSpark Distribution',
        'Developmental Editing to Polish Your Core Frameworks'
      ]
    },
    {
      id: 'celebrities',
      title: 'Celebrities, Influencers & Public Figures',
      badge: 'Legacy & Brand',
      icon: <Crown className="w-6 h-6 text-purple-500" />,
      tagline: 'Preserve Your Legacy with Archival-Grade Memoirs & Official Biographies',
      description: 'Establish ultimate authority and preserve your journey with VIP ghostwriting assistance, high-DPI full-color photographic inserts, and luxury coffee-table hardcover production.',
      sampleBooks: ['Behind the Spotlight', 'My Unfiltered Journey', 'Building an Empire'],
      highlights: [
        'VIP Ghostwriting & Oral History Transcription Services',
        'High-Gloss Full-Color Photo Signature Inserts',
        'Luxury Cloth-Bound Hardcovers with Foil-Stamped Jackets',
        'Strict NDA & Confidentiality Protection'
      ]
    },
    {
      id: 'poets',
      title: 'Poets & Creative Writers',
      badge: 'Artistic Excellence',
      icon: <Feather className="w-6 h-6 text-teal-500" />,
      tagline: 'Elegant Typography & Spacious Layouts Designed specifically for Verses & Prose',
      description: 'Poetry requires delicate typesetting, intentional whitespace, and custom paper selection. We treat every poem like artwork, ensuring precise alignment and visual rhythm on every page.',
      sampleBooks: ['Echoes in Silence', 'Verses of the Heart', 'Midnight Musings'],
      highlights: [
        'Bespoke Minimalist Interior Typography (Garamond, Bodoni, Caslon)',
        'Cream Natural Uncoated Antique Paper Stock',
        'Custom Hand-Drawn Vector Line-Art Illustrations',
        'Pocket-Sized & Square Hardcover Editions'
      ]
    },
    {
      id: 'fiction',
      title: 'Novelists & Fiction Writers',
      badge: 'World Building',
      icon: <BookMarked className="w-6 h-6 text-indigo-500" />,
      tagline: 'Captivate Readers with Custom Chapter Ornaments & Mass Market Paperback Formats',
      description: 'Whether writing Sci-Fi, Fantasy, Romance, Thrillers, or Historical Fiction, bring your fictional universe to life with custom chapter headers, map illustrations, and flawless typesetting.',
      sampleBooks: ['Chronicles of the Starlight Realm', 'The Crimson Key', 'Shadows of Tomorrow'],
      highlights: [
        'Custom Chapter Drop-Caps & Ornamental Scene Dividers',
        'Map Vector Clean-Up & World-Building Illustrations',
        'Reflowable EPUB3 E-Book Formatting for Amazon Kindle & Apple Books',
        'Mass-Market & Trade Paperback Print-on-Demand Setup'
      ]
    },
    {
      id: 'magazines',
      title: 'Project Books, Magazines & Photobooks',
      badge: 'Visual Showcase',
      icon: <Camera className="w-6 h-6 text-blue-500" />,
      tagline: 'Vibrant Full-Color CMYK Layouts, Coil-Bound Workbooks & Glossy Catalogs',
      description: 'For visual creators, architects, photographers, fitness trainers, and recipe book authors. Ultra-crisp 300 DPI CMYK color reproduction with lay-flat spiral or perfect-bound finishing.',
      sampleBooks: ['Architectural Spaces 2026', 'Gourmet Plant-Based Recipes', 'The Craft Project Guide'],
      highlights: [
        '300 DPI High-Definition Color Management & Proofing',
        'Lay-Flat Spiral, Wire-O & Perfect Bound Magazine Finish',
        'Custom Grid Layouts & Multi-Column Article Spreads',
        'Short-Run Custom Print Batches from 50 to 5,000 Copies'
      ]
    }
  ];

  // Self-Publishing Services List
  const serviceOfferings = [
    {
      icon: <Feather className="w-6 h-6 text-blue-600" />,
      title: 'Ghostwriting & Editorial Polish',
      description: 'Have video transcripts, voice notes, or a raw draft? Our expert editors transform your ideas into polished, compelling chapters while preserving 100% of your voice.'
    },
    {
      icon: <Layout className="w-6 h-6 text-purple-600" />,
      title: 'Bespoke Cover & Interior Layout',
      description: 'No generic templates. Get custom Adobe InDesign interior typesetting, custom chapter ornamentation, and striking 3D cover renders that stand out on Amazon.'
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-emerald-600" />,
      title: '100% Rights & ISBN Ownership',
      description: 'We assign official ISBNs registered under YOUR name. You retain full copyright, all master files (PDF, EPUB, InDesign), and 100% of all future royalties.'
    },
    {
      icon: <CheckCircle2 className="w-6 h-6 text-teal-600" />,
      title: '100% PDF/UA & WCAG Accessible',
      badge: 'ISO 14289-1 Certified',
      description: 'All e-books and digital PDF deliverables are 100% PDF/UA (ISO 14289-1) and WCAG 2.1 AA compliant, formatted with logical reading order tags and alt-text for screen readers.'
    },
    {
      icon: <Globe className="w-6 h-6 text-indigo-600" />,
      title: 'Global Retail & Amazon Distribution',
      description: 'Your book published on Amazon KDP, IngramSpark, Barnes & Noble, Apple Books, Google Play, and available to 40,000+ bookstores worldwide.'
    },
    {
      icon: <Printer className="w-6 h-6 text-amber-600" />,
      title: 'On-Demand & Bulk Print Quality',
      description: 'Print-on-demand with ZERO inventory risk, plus heavy bulk discounts for author copies, live event book-signings, and channel merch sales.'
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-rose-600" />,
      title: 'Amazon Bestseller PR Campaign',
      description: 'Press releases, A+ Content design for Amazon product pages, social media media kits, and strategic launch support to achieve Bestseller badge status.'
    }
  ];

  // Publishing Journey Pipeline Steps
  const pipelineSteps = [
    {
      num: '01',
      title: 'Discovery & Vision Call',
      desc: 'We review your creator channel, manuscript idea, or raw draft. We define book format, target audience, and royalty goals.'
    },
    {
      num: '02',
      title: 'Editing & Voice Matching',
      desc: 'Our developmental editors polish your prose, refine chapter structure, and ensure your unique voice resonates throughout.'
    },
    {
      num: '03',
      title: 'Custom Cover & Interior Styling',
      desc: 'Our master typographers craft custom chapter headers, page layouts, and eye-catching cover artwork tailored to your genre.'
    },
    {
      num: '04',
      title: 'Author Proofing & Sample Copy',
      desc: 'Review high-resolution digital PDF proofs and receive an initial physical sample copy to touch, inspect, and approve.'
    },
    {
      num: '05',
      title: 'Global ISBN & Amazon Launch',
      desc: 'We register official ISBN barcodes, upload your e-book & print files to Amazon KDP & IngramSpark, and activate worldwide distribution.'
    },
    {
      num: '06',
      title: '100% Royalty Collection',
      desc: 'Watch royalties land directly into your bank account. You own 100% of all rights, master files, and lifetime book revenues.'
    }
  ];

  // FAQ Items
  const faqList = [
    {
      q: 'Do I keep 100% of my royalties and copyrights?',
      a: 'YES! Unlike traditional publishers or vanity presses, ASAP LEARNING takes ZERO percentage cut of your ongoing book royalties. You retain 100% copyright ownership, full distribution rights, and all master production files.'
    },
    {
      q: 'Can you help if I only have video transcripts or voice notes?',
      a: 'Absolutely! Many YouTubers and podcasters come to us with video transcripts, course outlines, or audio recordings. Our editorial team turns raw transcripts into well-structured, engaging book chapters.'
    },
    {
      q: 'How long does it take from raw draft to Amazon listing?',
      a: 'Our fast-track production timeline ranges between 7 to 21 days depending on manuscript length and service scope (editorial polish, cover design, and interior typesetting).'
    },
    {
      q: 'What book formats do you support?',
      a: 'We produce Hardcover (with dust jackets or printed case laminates), Trade Softcover Paperbacks, Mass Market Paperbacks, Spiral-Bound Project Books, Glossy Magazines, and EPUB3 / Kindle E-Books.'
    },
    {
      q: 'Can I order physical printed copies for my own events or merch store?',
      a: 'Yes! You can order as few as 10 copies or as many as 50,000 copies at ultra-low author print cost, shipped directly to your doorstep or warehouse.'
    },
    {
      q: 'Will my book be available in bookstores and on Amazon internationally?',
      a: 'Yes, through our global IngramSpark and Amazon KDP integration, your book will be listed across Amazon US, UK, India, Europe, Japan, Australia, as well as Barnes & Noble and over 40,000 library catalogs.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-600 selection:text-white">
      <SEO
        title="Creator & Self-Help Book Publishing Hub | ASAP LEARNING"
        description="Turn your story, voice, or content into a global bestseller. Professional self-publishing services for YouTubers, Celebrities, Authors, Poets, Biographers & Non-Fiction Creators. 100% Rights & Royalties Kept by You."
        keywords="Self-Help Book Publishing, YouTuber Book Publishing, Creator Book Production, Biography Ghostwriting, Novel Typesetting, Poetry Book Design, Amazon KDP Setup, Global Book Distribution, 100% Royalty Self Publishing"
        canonicalUrl="https://www.asaplearning.in/#self-publishing"
        ogImage="https://www.asaplearning.in/og-self-publishing.jpg"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24 bg-gradient-to-b from-blue-50/80 via-indigo-50/30 to-white border-b border-slate-200/80">
        {/* Glow ambient effects */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-200/40 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 right-10 w-[400px] h-[250px] bg-purple-200/30 blur-[100px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-wider shadow-xs">
              <Sparkles className="w-4 h-4 text-amber-500 animate-pulse" />
              <span>Bespoke Creator & Author Self-Publishing Studio</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.15]">
              Turn Your Voice, Story & Content Into an <span className="bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 bg-clip-text text-transparent">International Published Bestseller</span>
            </h1>

            <p className="text-base sm:text-xl text-slate-600 font-normal leading-relaxed max-w-3xl mx-auto">
              Tailored end-to-end publishing for <strong className="text-slate-900 font-semibold">Celebrities, YouTubers, Influencers, Novelists, Poets, Biographers, Self-Help Authors, Non-Fiction Leaders & Project Book Creators.</strong>
            </p>

            <div className="p-3.5 bg-white/90 border border-slate-200 rounded-2xl max-w-2xl mx-auto text-xs text-slate-700 flex items-center justify-center gap-3 shadow-sm">
              <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
              <span><strong className="text-slate-900 font-bold">100% Royalty & Copyright Ownership Kept by You.</strong> We take ZERO percentage cut from your book sales.</span>
            </div>

            {/* CTA Buttons */}
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#creator-form"
                className="w-full sm:w-auto px-8 py-4 rounded-2xl text-sm font-extrabold text-white bg-blue-600 hover:bg-blue-700 shadow-xl shadow-blue-600/20 transition-all transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                <BookOpen className="w-5 h-5 text-blue-100" />
                <span>Start Your Publishing Project</span>
                <ArrowRight className="w-4 h-4" />
              </a>

              <a
                href="#calculator"
                className="w-full sm:w-auto px-6 py-4 rounded-2xl text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-xs transition-all inline-flex items-center justify-center gap-2"
              >
                <Sliders className="w-4 h-4 text-blue-600" />
                <span>Calculate Royalty & Print Profit</span>
              </a>
            </div>

            {/* Creator Categories Badges */}
            <div className="pt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
              {[
                { label: '🌟 Celebrity & Influencer Memoirs', color: 'border-purple-200 bg-purple-50 text-purple-800' },
                { label: '🎥 YouTuber & Content Creator Playbooks', color: 'border-rose-200 bg-rose-50 text-rose-800' },
                { label: '💡 Self-Help & Personal Growth', color: 'border-amber-200 bg-amber-50 text-amber-800' },
                { label: '📖 Non-Fiction & Executive Guides', color: 'border-blue-200 bg-blue-50 text-blue-800' },
                { label: '🖋️ Fiction, Novels & Fantasy', color: 'border-indigo-200 bg-indigo-50 text-indigo-800' },
                { label: '🪶 Poetry & Anthology Collections', color: 'border-teal-200 bg-teal-50 text-teal-800' },
                { label: '🎨 Project Books & Glossy Magazines', color: 'border-cyan-200 bg-cyan-50 text-cyan-800' },
                { label: '📜 Biographies & Life Histories', color: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
              ].map((badge, idx) => (
                <span
                  key={idx}
                  className={`px-3 py-1.5 rounded-xl border text-xs font-semibold shadow-2xs ${badge.color}`}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          </div>

          {/* Quick Metrics Banner */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-white/90 border border-slate-200 rounded-3xl shadow-sm backdrop-blur-md">
            <div className="text-center p-3 border-r border-slate-200 last:border-0">
              <p className="text-2xl sm:text-3xl font-black text-slate-900">100%</p>
              <p className="text-xs text-slate-500 font-medium mt-1">Author Royalty Retained</p>
            </div>
            <div className="text-center p-3 border-r border-slate-200 last:border-0">
              <p className="text-2xl sm:text-3xl font-black text-blue-600">1,200+</p>
              <p className="text-xs text-slate-500 font-medium mt-1">Books & Publications Delivered</p>
            </div>
            <div className="text-center p-3 border-r border-slate-200 last:border-0">
              <p className="text-2xl sm:text-3xl font-black text-purple-600">7 - 14 Days</p>
              <p className="text-xs text-slate-500 font-medium mt-1">Fast-Track Turnaround</p>
            </div>
            <div className="text-center p-3">
              <p className="text-2xl sm:text-3xl font-black text-emerald-600">40,000+</p>
              <p className="text-xs text-slate-500 font-medium mt-1">Global Amazon & Store Outlets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Navigation Component Card */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-6">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3.5 sm:p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 text-xs font-bold w-full md:w-auto overflow-hidden">
            <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[11px] mr-1 hidden sm:inline shrink-0">
              Quick Navigation:
            </span>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5 w-full">
              <a
                href="#publishing-services"
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 transition-all flex items-center gap-1.5 border border-slate-200/80 font-bold text-xs whitespace-nowrap shadow-2xs"
              >
                <Layers className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span>Publishing Services</span>
              </a>

              <a
                href="#author-perks"
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-purple-50 text-slate-700 hover:text-purple-700 transition-all flex items-center gap-1.5 border border-slate-200/80 font-bold text-xs whitespace-nowrap shadow-2xs"
              >
                <Award className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                <span>Author Perks & Royalties</span>
              </a>

              <a
                href="#roadmap"
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 transition-all flex items-center gap-1.5 border border-slate-200/80 font-bold text-xs whitespace-nowrap shadow-2xs"
              >
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>6-Step Roadmap</span>
              </a>

              <a
                href="#calculator"
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-amber-50 text-slate-700 hover:text-amber-700 transition-all flex items-center gap-1.5 border border-slate-200/80 font-bold text-xs whitespace-nowrap shadow-2xs"
              >
                <Sliders className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span>Royalty Calculator</span>
              </a>

              <a
                href="#creator-form"
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 transition-all flex items-center gap-1.5 border border-slate-200/80 font-bold text-xs whitespace-nowrap shadow-2xs"
              >
                <Send className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span>Contact & Inquiry</span>
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto justify-end">
            <a
              href="#creator-form"
              onClick={(e) => {
                e.preventDefault();
                setFormData(prev => ({
                  ...prev,
                  creatorType: 'Celebrity / Public Figure / Influencer'
                }));
                const el = document.getElementById('creator-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-4 py-2 rounded-xl text-xs font-extrabold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all inline-flex items-center gap-1.5 shadow-xs cursor-pointer whitespace-nowrap"
            >
              <Crown className="w-3.5 h-3.5 text-amber-300" />
              <span>VIP Consultation</span>
            </a>
          </div>
        </div>
      </div>

      {/* Creator Archetypes Selector Tabs */}
      <section className="py-16 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Designed for Every Type of Creator & Author
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              Select your category below to explore custom publishing solutions tailored specifically to your genre and fanbase.
            </p>
          </div>

          {/* Tab Selection Navigation */}
          <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
            {creatorArchetypes.map((arch) => (
              <button
                key={arch.id}
                onClick={() => setActiveTab(arch.id as any)}
                className={`px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 border ${
                  activeTab === arch.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200/80 hover:text-slate-900'
                }`}
              >
                {arch.icon}
                <span>{arch.title}</span>
              </button>
            ))}
          </div>

          {/* Active Tab Display Card */}
          {(() => {
            const activeData = creatorArchetypes.find((a) => a.id === activeTab) || creatorArchetypes[0];
            return (
              <div className="bg-gradient-to-br from-blue-50/50 via-slate-50 to-indigo-50/30 border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xl relative overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7 space-y-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 border border-blue-200 text-blue-800 text-xs font-bold">
                      {activeData.icon}
                      <span>{activeData.badge}</span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                      {activeData.title}
                    </h3>

                    <p className="text-base text-blue-700 font-medium italic">
                      "{activeData.tagline}"
                    </p>

                    <p className="text-sm text-slate-600 leading-relaxed">
                      {activeData.description}
                    </p>

                    <div className="space-y-2.5 pt-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Key Features Included:</p>
                      {activeData.highlights.map((item, i) => (
                        <div key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 flex items-center gap-3">
                      <a
                        href="#creator-form"
                        className="px-5 py-2.5 rounded-xl text-xs font-extrabold text-white bg-blue-600 hover:bg-blue-700 transition-colors inline-flex items-center gap-1.5 shadow-sm"
                      >
                        <span>Request {activeData.title} Package</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>

                  {/* Visual Mockup Box */}
                  <div className="lg:col-span-5 bg-white border border-slate-200/90 rounded-2xl p-6 relative shadow-sm">
                    <div className="absolute top-3 right-3 px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold rounded-lg uppercase">
                      Sample Book Styles
                    </div>

                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Popular Title Formats:</p>

                    <div className="space-y-3">
                      {activeData.sampleBooks.map((bookTitle, idx) => (
                        <div
                          key={idx}
                          className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl flex items-center gap-3 hover:border-blue-400 transition-colors"
                        >
                          <div className="w-8 h-10 rounded bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shrink-0 text-white font-black text-xs shadow-xs">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">"{bookTitle}"</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">Custom InDesign Layout & 3D Cover Included</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-xl text-[11px] text-blue-900 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                      <span>Free 3D book cover render provided before printing starts.</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Complete End-to-End Services Spectrum */}
      <section id="publishing-services" className="py-16 sm:py-24 bg-slate-50/80 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 border border-purple-200 text-purple-700 text-xs font-bold uppercase mb-3">
              <Layers className="w-3.5 h-3.5" />
              <span>Full-Spectrum Publishing Services</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Everything You Need to Become a Published Author
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              From raw manuscript polish to global print distribution, we manage the technical heavy lifting while you keep 100% ownership.
            </p>
          </div>

          {/* 100% PDF/UA Compliant Badge Banner */}
          <div className="mb-12 p-6 rounded-3xl bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white border border-blue-500/30 shadow-xl relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-1/3 w-60 h-60 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-start sm:items-center gap-4 relative z-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0 shadow-inner text-emerald-300">
                <ShieldCheck className="w-8 h-8 sm:w-9 sm:h-9 text-emerald-400" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>100% PDF/UA Compliant Deliverables</span>
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-[11px] font-bold uppercase tracking-wider">
                    ISO 14289-1 Standard
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-[11px] font-bold uppercase tracking-wider">
                    WCAG 2.1 Level AA
                  </span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  Professional Universal Accessibility Standard Guaranteed
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 max-w-3xl leading-relaxed">
                  Every e-book (EPUB3) and digital PDF published through ASAP LEARNING is engineered with semantic reading order tags, figure alt-text structures, and screen reader compatibility (NVDA, JAWS, VoiceOver).
                </p>
              </div>
            </div>

            <div className="shrink-0 flex items-center gap-3 w-full lg:w-auto relative z-10">
              <a
                href="#creator-form"
                onClick={(e) => {
                  e.preventDefault();
                  if (!formData.servicesNeeded.includes('E-Book EPUB3 & Kindle Formatting')) {
                    setFormData(prev => ({
                      ...prev,
                      servicesNeeded: [...prev.servicesNeeded, 'E-Book EPUB3 & Kindle Formatting']
                    }));
                  }
                  const el = document.getElementById('creator-form');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full lg:w-auto px-5 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs sm:text-sm transition-all shadow-lg flex items-center justify-center gap-2 transform hover:-translate-y-0.5 cursor-pointer"
              >
                <Award className="w-4 h-4 text-slate-950" />
                <span>Request Accessibility Audit</span>
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceOfferings.map((service, idx) => (
              <div
                key={idx}
                className="p-6 bg-white border border-slate-200/80 rounded-2xl hover:border-blue-300 transition-all hover:-translate-y-1 shadow-xs hover:shadow-md group relative"
              >
                {service.badge && (
                  <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-extrabold uppercase">
                    {service.badge}
                  </span>
                )}
                <div className="p-3 bg-slate-100 rounded-xl w-fit mb-4 border border-slate-200 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Royalty & Print Profit Calculator */}
      <section id="calculator" className="py-16 sm:py-24 bg-white border-b border-slate-200/80 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase mb-3">
              <DollarSign className="w-3.5 h-3.5" />
              <span>Interactive Author Royalty Calculator</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              See How Much You Earn Per Copy
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              With 100% Royalty Ownership, you keep every rupee/dollar after basic printing costs.
            </p>
          </div>

          <div className="bg-slate-50/80 border border-slate-200 rounded-3xl p-6 sm:p-10 max-w-5xl mx-auto shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Controls Column */}
              <div className="lg:col-span-6 space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Select Book Format:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'paperback', label: 'Softcover Paperback', cost: '₹120' },
                      { id: 'hardcover', label: 'Hardcover (Dust Jacket)', cost: '₹220' },
                      { id: 'ebook', label: 'E-Book / Kindle Edition', cost: '₹0' },
                      { id: 'bundle', label: 'Paperback + E-Book Bundle', cost: '₹240' }
                    ].map((fmt) => (
                      <button
                        key={fmt.id}
                        onClick={() => setBookFormat(fmt.id as any)}
                        className={`p-3 rounded-xl border text-left text-xs font-bold transition-all ${
                          bookFormat === fmt.id
                            ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300'
                        }`}
                      >
                        <p>{fmt.label}</p>
                        <p className={`text-[10px] font-normal mt-0.5 ${bookFormat === fmt.id ? 'text-blue-100' : 'text-slate-500'}`}>
                          Est. Print: {fmt.cost}/copy
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-2">
                    <label htmlFor="sp-book-price">Retail Selling Price (INR ₹):</label>
                    <span className="text-blue-600 font-mono text-sm">₹{bookPrice}</span>
                  </div>
                  <input
                    id="sp-book-price"
                    type="range"
                    min="199"
                    max="1999"
                    step="50"
                    value={bookPrice}
                    aria-label="Retail Selling Price in INR"
                    onChange={(e) => setBookPrice(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
                    <span>₹199</span>
                    <span>₹999</span>
                    <span>₹1,999</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-bold text-slate-700 mb-2">
                    <label htmlFor="sp-sales-volume">Target Sales Volume (Copies):</label>
                    <span className="text-purple-600 font-mono text-sm">{salesVolume.toLocaleString()} copies</span>
                  </div>
                  <input
                    id="sp-sales-volume"
                    type="range"
                    min="100"
                    max="10000"
                    step="100"
                    value={salesVolume}
                    aria-label="Target Sales Volume in Copies"
                    onChange={(e) => setSalesVolume(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
                    <span>100</span>
                    <span>2,500</span>
                    <span>10,000</span>
                  </div>
                </div>
              </div>

              {/* Output Results Column */}
              <div className="lg:col-span-6 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6 relative overflow-hidden shadow-md">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100/50 rounded-full blur-2xl pointer-events-none" />

                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Estimated Financial Summary</p>

                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Total Gross Sales:</span>
                    <span className="text-base font-extrabold text-slate-900 font-mono">₹{grossRevenue.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-xs text-slate-500">Est. Total Print Cost:</span>
                    <span className="text-sm font-semibold text-rose-600 font-mono">- ₹{totalPrintCost.toLocaleString()}</span>
                  </div>

                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1">
                    <p className="text-xs font-bold text-emerald-800 uppercase">Your Net Author Profit (100% Yours):</p>
                    <p className="text-3xl font-black text-emerald-700 font-mono">
                      ₹{netProfit.toLocaleString()}
                    </p>
                    <p className="text-[11px] text-emerald-900 font-medium pt-1">
                      Profit Margin: <strong className="text-emerald-950 font-bold">{profitMargin}%</strong> per book sold!
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => onOpenContact(`Royalty Estimate Inquiry for ${salesVolume} copies @ ₹${bookPrice}`)}
                  className="w-full py-3 rounded-xl text-xs font-extrabold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 transition-all inline-flex items-center justify-center gap-2 shadow-md"
                >
                  <span>Lock In This Profit Structure</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6-Step Publishing Pipeline */}
      <section id="roadmap" className="py-16 sm:py-24 bg-slate-50/80 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              The 6-Step Author Publishing Roadmap
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              How we transform your manuscript or raw content into an Amazon-listed physical book.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pipelineSteps.map((step, idx) => (
              <div
                key={idx}
                className="p-6 bg-white border border-slate-200 rounded-2xl relative space-y-3 shadow-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-black text-blue-600/30 font-mono">{step.num}</span>
                  <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold border border-slate-200">
                    Phase {idx + 1}
                  </span>
                </div>
                <h3 className="text-base font-extrabold text-slate-900">{step.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table: Traditional vs Self-Publishing vs ASAP LEARNING */}
      <section id="author-perks" className="py-16 sm:py-24 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Why Creators Choose ASAP LEARNING
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              Compare our author-first model against traditional publishing houses and generic self-publishing aggregators.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm border-collapse min-w-[650px] shadow-sm rounded-2xl overflow-hidden border border-slate-200">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 uppercase font-bold text-[11px] bg-slate-100/80">
                  <th className="py-4 px-4 bg-slate-100">Publishing Feature</th>
                  <th className="py-4 px-4 bg-slate-50 text-slate-500">Traditional Houses</th>
                  <th className="py-4 px-4 bg-slate-50 text-slate-500">Generic Aggregators</th>
                  <th className="py-4 px-4 bg-blue-50 text-blue-900 border-t-2 border-blue-600 font-extrabold">ASAP LEARNING Creator Hub</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {[
                  { feat: 'Author Royalty Cut', trad: '8% - 15% Only', gen: '30% - 50% Royalties Taken', asap: '100% Royalty Kept by Author (0% Cut)' },
                  { feat: 'Copyright & ISBN Ownership', trad: 'Publisher Owns Rights', gen: 'Shared / Generic ISBN', asap: '100% Author Owned' },
                  { feat: 'Production Turnaround', trad: '12 - 18 Months', gen: '30 - 60 Days', asap: '7 - 14 Days Fast-Track' },
                  { feat: 'Typesetting & Layout Quality', trad: 'Standard Template', gen: 'Automated Bot Layout', asap: 'Bespoke InDesign Craftsmen' },
                  { feat: 'Ghostwriting & Voice Polish', trad: 'Rarely Provided', gen: 'Not Offered', asap: 'VIP Creator Ghostwriters' },
                  { feat: 'Print-On-Demand + Bulk Orders', trad: 'High Inventory Costs', gen: 'High Unit Cost', asap: 'Ultra-Low Author Print Cost' }
                ].map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-4 font-bold text-slate-900 bg-slate-50/50">{row.feat}</td>
                    <td className="py-4 px-4 text-slate-500">{row.trad}</td>
                    <td className="py-4 px-4 text-slate-500">{row.gen}</td>
                    <td className="py-4 px-4 font-bold text-blue-900 bg-blue-50/50 border-x border-blue-200">
                      <div className="flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>{row.asap}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Dedicated Creator & Author Application Form */}
      <section id="creator-form" className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 via-blue-50/30 to-white border-b border-slate-200/80">
        <div id="contact" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold uppercase">
              <Send className="w-3.5 h-3.5" />
              <span>Direct Author Inquiry</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Publish Your Book with ASAP LEARNING
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Fill out your project details below and an Executive Publishing Director will connect with you within 24 hours.
            </p>
          </div>

          {formSubmitted ? (
            <div className="p-8 sm:p-12 bg-white border border-emerald-300 rounded-3xl text-center space-y-4 shadow-lg animate-in fade-in duration-300">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-200">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-slate-900">Publishing Inquiry Received!</h3>
              <p className="text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
                Thank you, <strong className="text-slate-900">{formData.authorName}</strong>. All project details have been transmitted directly to <strong className="text-blue-700">contact@asaplearning.in</strong> for <strong className="text-blue-600">"{formData.workingTitle || 'Your Book Project'}"</strong>. Our executive publishing team will review your manuscript status and reply to <strong className="text-slate-900">{formData.email}</strong> within 24 hours.
              </p>
              <div className="pt-4 flex items-center justify-center gap-3">
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-colors"
                >
                  Submit Another Project
                </button>
                <a
                  href={`https://wa.me/${COMPANY_INFO.whatsappNumber}?text=Hi%20ASAP%20LEARNING%2C%20I%20just%20submitted%20my%20author%20inquiry%20for%20${encodeURIComponent(formData.workingTitle || 'my book')}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors inline-flex items-center gap-1.5 shadow-sm"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Instant WhatsApp Follow-Up</span>
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="p-6 sm:p-10 bg-white border border-slate-200 rounded-3xl space-y-6 shadow-xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="sp-author-name" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Your Name / Pen Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="sp-author-name"
                    type="text"
                    required
                    placeholder="e.g. Alex Morgan or Dr. R. K. Sharma"
                    value={formData.authorName}
                    onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="sp-author-email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="sp-author-email"
                    type="email"
                    required
                    placeholder="author@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="sp-author-phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Phone / WhatsApp Number
                  </label>
                  <input
                    id="sp-author-phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="sp-creator-type" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Creator Profile Category
                  </label>
                  <select
                    id="sp-creator-type"
                    value={formData.creatorType}
                    onChange={(e) => setFormData({ ...formData, creatorType: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  >
                    <option value="YouTuber / Content Creator">YouTuber / Content Creator</option>
                    <option value="Celebrity / Public Figure / Influencer">Celebrity / Public Figure / Influencer</option>
                    <option value="Self-Help / Personal Growth Coach">Self-Help / Personal Growth Coach</option>
                    <option value="Novelist / Fiction Writer">Novelist / Fiction Writer</option>
                    <option value="Poet / Verse Artist">Poet / Verse Artist</option>
                    <option value="Executive / Business Leader">Executive / Business Leader</option>
                    <option value="Memoir / Biography Subject">Memoir / Biography Subject</option>
                    <option value="Magazine / Project Book / Photobook">Magazine / Project Book / Photobook</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="sp-working-title" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Working Book Title (or Topic)
                  </label>
                  <input
                    id="sp-working-title"
                    type="text"
                    placeholder="e.g. Master Your Mindset in 30 Days"
                    value={formData.workingTitle}
                    onChange={(e) => setFormData({ ...formData, workingTitle: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="sp-manuscript-status" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Manuscript Status
                  </label>
                  <select
                    id="sp-manuscript-status"
                    value={formData.manuscriptStatus}
                    onChange={(e) => setFormData({ ...formData, manuscriptStatus: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                  >
                    <option value="Complete Manuscript Ready">Complete Manuscript Ready</option>
                    <option value="Rough Draft / In Progress">Rough Draft / In Progress</option>
                    <option value="Video Transcripts / Notes Only">Video Transcripts / Notes Only</option>
                    <option value="Idea / Concept Phase">Idea / Concept Phase</option>
                    <option value="Already Published (Re-typeset)">Already Published (Need Better Formatting)</option>
                  </select>
                </div>
              </div>

              {/* Service Checkboxes */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Select Required Services:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {[
                    'Ghostwriting & Content Polish',
                    'Developmental Editing & Proofreading',
                    'Cover & Interior Design (InDesign)',
                    'Global Distribution (Amazon & IngramSpark)',
                    'Official ISBN & Copyright Setup',
                    'Bulk Printing & Physical Copies',
                    'Amazon Bestseller PR Campaign',
                    'E-Book EPUB3 & Kindle Formatting'
                  ].map((service, idx) => (
                    <label
                      key={idx}
                      className={`p-3 rounded-xl border text-xs font-semibold flex items-center gap-2.5 cursor-pointer transition-colors ${
                        formData.servicesNeeded.includes(service)
                          ? 'bg-blue-50 border-blue-500 text-blue-900'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.servicesNeeded.includes(service)}
                        onChange={() => handleCheckboxToggle(service)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="sp-notes" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Additional Details or Goals
                </label>
                <textarea
                  id="sp-notes"
                  rows={3}
                  placeholder="Tell us about your audience size, launch timeline, or specific design preferences..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-2xl text-sm font-extrabold text-white bg-blue-600 hover:bg-blue-700 shadow-xl shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4 text-blue-100" />
                <span>Submit Publishing Project Inquiry</span>
              </button>
            </form>
          )}
        </div>
      </section>

      {/* VIP Call-to-Action Section for Authors, Celebrities & Creative Professionals */}
      <section id="vip-consultation" className="py-16 sm:py-24 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white relative overflow-hidden border-y border-slate-800">
        {/* Ambient Glows */}
        <div className="absolute top-1/2 left-10 -translate-y-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-12 backdrop-blur-md shadow-2xl">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold uppercase tracking-wider">
                <Crown className="w-4 h-4 text-amber-400" />
                <span>Bespoke VIP Publishing for Creators, Celebrities & Authors</span>
              </div>

              <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
                Ready to Turn Your Vision into an <span className="bg-gradient-to-r from-amber-300 via-rose-300 to-purple-300 bg-clip-text text-transparent">International Bestseller?</span>
              </h2>

              <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal max-w-3xl mx-auto">
                Whether you are a <strong className="text-white">celebrity releasing a memoir, a YouTuber publishing an executive playbook, a novelist, a poet, or an independent author</strong>, our senior publishing directors provide white-glove, 1-on-1 consultations tailored to your timeline and brand.
              </p>

              {/* Value Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-left pt-4">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                  <div className="p-2 rounded-xl bg-amber-500/20 text-amber-300 w-fit">
                    <Crown className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-extrabold text-white">Dedicated VIP Director</h3>
                  <p className="text-xs text-slate-300 leading-normal">Single point of contact managing ghostwriting, InDesign layout & distribution.</p>
                </div>

                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                  <div className="p-2 rounded-xl bg-blue-500/20 text-blue-300 w-fit">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-extrabold text-white">Full NDA Protection</h3>
                  <p className="text-xs text-slate-300 leading-normal">Strict confidentiality agreements covering your draft, manuscript & identity.</p>
                </div>

                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                  <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-300 w-fit">
                    <Award className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-extrabold text-white">100% Royalty Kept</h3>
                  <p className="text-xs text-slate-300 leading-normal">Zero percentage cut on sales. You keep every rupee/dollar of profit.</p>
                </div>

                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                  <div className="p-2 rounded-xl bg-purple-500/20 text-purple-300 w-fit">
                    <Globe className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-extrabold text-white">Global Retail Outlets</h3>
                  <p className="text-xs text-slate-300 leading-normal">Amazon KDP, IngramSpark, Flipkart, Barnes & Noble and Google Books listing.</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => onOpenContact('VIP Author & Celebrity Publishing Consultation')}
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl text-sm font-extrabold text-slate-900 bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 hover:from-amber-200 hover:to-amber-300 shadow-xl shadow-amber-500/20 transition-all transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
                >
                  <Crown className="w-5 h-5 text-slate-900" />
                  <span>Book 1-on-1 VIP Consultation</span>
                  <ArrowRight className="w-4 h-4 text-slate-900" />
                </button>

                <a
                  href={`https://wa.me/${COMPANY_INFO.whatsappNumber}?text=Hi%20ASAP%20LEARNING%2C%20I%20am%20interested%20in%20a%20VIP%20Self-Publishing%20Consultation%20for%20my%20book.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-6 py-4 rounded-2xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-all inline-flex items-center justify-center gap-2 border border-emerald-500/50 shadow-lg"
                >
                  <MessageSquare className="w-4 h-4 text-white" />
                  <span>Chat Directly on WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-24 bg-slate-50/90">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Self-Publishing Questions Answered
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              Everything you need to know about keeping 100% rights, ISBNs, and print production.
            </p>
          </div>

          <div className="space-y-3">
            {faqList.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-colors shadow-2xs"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-sm text-slate-900 hover:text-blue-600 transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${activeFaq === idx ? 'rotate-180 text-blue-600' : ''}`} />
                </button>

                {activeFaq === idx && (
                  <div className="px-5 pb-5 pt-0 text-xs sm:text-sm text-slate-600 border-t border-slate-100 pt-3 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Sticky CTA Banner */}
      <footer className="py-10 pb-24 sm:pb-10 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-base font-extrabold text-slate-900">Ready to publish your book?</p>
            <p className="text-xs text-slate-500 mt-0.5">ASAP LEARNING Author & Creator Publishing Studio</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onBackToHome}
              className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors"
            >
              Main Homepage
            </button>
            <a
              href="#creator-form"
              className="px-5 py-2 rounded-xl text-xs font-extrabold text-white bg-blue-600 hover:bg-blue-700 transition-colors inline-flex items-center gap-1.5 shadow-sm"
            >
              <span>Get Started Now</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </footer>

      {/* Floating Bottom Navigation Bar (Mobile Devices Only) */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 py-2 px-3 sm:hidden shadow-2xl">
        <div className="flex items-center justify-around gap-1 max-w-md mx-auto">
          <a
            href="#publishing-services"
            className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-300 hover:text-white transition-colors py-1 px-2.5 rounded-xl active:bg-slate-800"
          >
            <div className="p-1 rounded-lg bg-blue-500/20 text-blue-400">
              <Layers className="w-4 h-4" />
            </div>
            <span>Services</span>
          </a>

          <a
            href="#calculator"
            className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-300 hover:text-white transition-colors py-1 px-2.5 rounded-xl active:bg-slate-800"
          >
            <div className="p-1 rounded-lg bg-amber-500/20 text-amber-400">
              <Sliders className="w-4 h-4" />
            </div>
            <span>Pricing</span>
          </a>

          <a
            href="#author-perks"
            className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-300 hover:text-white transition-colors py-1 px-2.5 rounded-xl active:bg-slate-800"
          >
            <div className="p-1 rounded-lg bg-purple-500/20 text-purple-400">
              <Award className="w-4 h-4" />
            </div>
            <span>Perks</span>
          </a>

          <button
            onClick={() => onOpenContact('Self-Publishing Mobile Quick Inquiry')}
            className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-white transition-colors py-1 px-2.5 rounded-xl active:bg-slate-800"
          >
            <div className="p-1 rounded-lg bg-blue-600 text-white shadow-xs">
              <Send className="w-4 h-4" />
            </div>
            <span className="text-blue-300">Contact</span>
          </button>
        </div>
      </div>
    </div>
  );
};
